import { format } from 'date-fns';
import { ForwardedRef, forwardRef } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { FETCH_INVOICE_LIST } from '~/actions';
import { Box, Column, Link, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { DataGrid } from '~/commonComponents';
import { useAppSelector, useLoading } from '~/hooks';
import { currencyFormat } from '~/utils/currencyFormat';
import type { TViewedOfficeScope } from '~/types/ViewedOfficeScope';
import { useSelectScopeOffice } from '../../hooks/useSelectScopeOffice';
import { IInvoice, PaymentRecordType } from '~/models';
import { routes } from '~/routes';
import { Colors } from '~/themes/colors';

interface IPaymentInvoiceTableProps {
  onScroll: () => void;
  /** The offices the list is for; a row opens as one of them (GOTU-19472). */
  scope: TViewedOfficeScope;
}

const getPaymentRecordTypeColors = (type: PaymentRecordType) => {
  switch (type) {
    case PaymentRecordType.ListingFee:
      return Colors.jobs_listing_fee_background;
    case PaymentRecordType.OnsiteInterview:
      return Colors.jobs_interview_background;
    default:
      return Colors.jobs_shift_background;
  }
};

const getPaymentRecordTypeText = (type: PaymentRecordType) => {
  switch (type) {
    case PaymentRecordType.ListingFee:
      return 'LISTING FEE';
    case PaymentRecordType.OnsiteInterview:
      return 'INTERVIEW';
    default:
      return 'SHIFT';
  }
};

export const PaymentInvoiceTable = forwardRef<HTMLDivElement, IPaymentInvoiceTableProps>(
  ({ onScroll, scope }, ref: ForwardedRef<HTMLDivElement>) => {
    const navigate = useNavigate();
    const selectScopeOffice = useSelectScopeOffice(scope);

    const invoices: IInvoice[] = useAppSelector((state) => state.job.invoices);
    const { isLoading } = useLoading([FETCH_INVOICE_LIST]);

    return (
      <Column
        ref={ref}
        onScroll={onScroll}
        style={{ marginTop: 32, maxHeight: 600, overflowY: 'scroll', width: '100%' }}
      >
        <DataGrid
          EmptyComponent={<Typography as="span" textAlign="center">There are no invoices.</Typography>}
          columns={[
            {
              key: 'view',
              header: 'View',
              width: '140px',
              render: (item) => (
                <Box
                  style={{
                    alignSelf: 'flex-start',
                    backgroundColor: getPaymentRecordTypeColors(item.paymentRecordType),
                    borderRadius: 8,
                    cursor: 'pointer',
                    padding: '2px 10px',
                  }}
                  onClick={() => {
                    selectScopeOffice();

                    if (item.paymentRecordType === PaymentRecordType.ListingFee) {
                      navigate(generatePath(routes.jobOverview, { postingId: item.job_id }));
                      return;
                    }

                    navigate(`/dashboard/job/${item?.job_id}`, {
                      state: { job_id: item?.job_id, jobId: item?.job_id },
                    });
                  }}
                >
                  <Typography as="span" color={colors.neutral[500]} style={{ textDecoration: 'underline' }}>
                    {getPaymentRecordTypeText(item.paymentRecordType)}
                  </Typography>
                </Box>
              ),
            },
            {
              key: 'office',
              header: 'Office',
              render: (item) => <Typography as="span">{item?.office_name}</Typography>,
            },
            {
              key: 'amount',
              header: 'Amount',
              width: '140px',
              render: (item) => <Typography as="span" bold>${currencyFormat(item.amount)}</Typography>,
            },
            {
              key: 'posting_date',
              header: 'Posting Date',
              width: '200px',
              render: (item) => (
                <Typography as="span">
                  {item.jobDate ? format(new Date(item.jobDate), 'EEE, MMM dd, yyyy') : '-'}
                </Typography>
              ),
            },
            {
              key: 'invoice_date',
              header: 'Invoice Date',
              width: '200px',
              render: (item) => (
                <Column>
                  <Typography as="span">{item.date ? format(new Date(item.date), 'EEE, MMM dd, yyyy') : '-'}</Typography>
                  <Typography as="span">{item.date ? format(new Date(item.date), 'hh:mm a') : '-'}</Typography>
                </Column>
              ),
            },
            {
              key: 'invoice_#',
              header: 'Invoice #',
              width: '140px',
              render: (item) => (
                <Link
                  href={item.invoice_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="inherit"
                >
                  <Typography as="span" bold color={colors.secondary[500]} size="body_small">
                    {item.invoice_number || '-'}
                  </Typography>
                </Link>
              ),
            },
          ]}
          data={invoices}
          keyExtractor={(it) => it.transaction_id}
          isLoading={isLoading}
          isStickyHeader
        />
      </Column>
    );
  },
);

PaymentInvoiceTable.displayName = 'PaymentInvoiceTable';
